import React from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, AreaChart, Area } from 'recharts';
import { useAppContext } from '../../hooks/useAppContext';
import { FileText, CheckCircle, MessageCircle, DollarSign, TrendingUp, Calendar, ArrowRight, BarChart2, Activity, PieChart as PieChartIcon } from 'lucide-react';

const employeePerformanceData = [
    { name: 'عبدالله', "التقارير المقدمة": 40, "أعمال الصيانة": 24, "أعمال التركيب": 18, "الاستفسارات المعالجة": 35 },
    { name: 'فاطمة', "التقارير المقدمة": 30, "أعمال الصيانة": 13, "أعمال التركيب": 22, "الاستفسارات المعالجة": 20 },
    { name: 'محمد', "التقارير المقدمة": 20, "أعمال الصيانة": 48, "أعمال التركيب": 12, "الاستفسارات المعالجة": 15 },
    { name: 'سارة', "التقارير المقدمة": 27, "أعمال الصيانة": 39, "أعمال التركيب": 8, "الاستفسارات المعالجة": 28 },
];

const revenueData = [
    { name: 'الأسبوع 1', 'الإيرادات': 40000 },
    { name: 'الأسبوع 2', 'الإيرادات': 30000 },
    { name: 'الأسبوع 3', 'الإيرادات': 55000 },
    { name: 'الأسبوع 4', 'الإيرادات': 48000 },
];

const customerFeedbackData = [
    { name: 'إيجابي', value: 400 },
    { name: 'محايد', value: 120 },
    { name: 'سلبي', value: 35 },
];
const FEEDBACK_COLORS = ['#10b981', '#f97316', '#ef4444'];

const branchRevenueData = [
    { name: 'الرياض', 'الإيرادات': 120000 },
    { name: 'جدة', 'الإيرادات': 95000 },
    { name: 'الدمام', 'الإيرادات': 75000 },
    { name: 'مكة', 'الإيرادات': 40000 },
]

const kpiCards = [
    { title: 'التقارير المقدمة', value: '315', icon: FileText, color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20' },
    { title: 'الأعمال الفنية', value: '180', icon: CheckCircle, color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' },
    { title: 'الاستفسارات المعالجة', value: '98', icon: MessageCircle, color: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/20' },
    { title: 'استفسارات الأسعار', value: '75', icon: DollarSign, color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20' },
    { title: 'المبيعات الناجحة', value: '42', icon: TrendingUp, color: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/20' },
    { title: 'المواعيد القادمة', value: '12', icon: Calendar, color: 'text-rose-400', bg: 'bg-rose-500/10', border: 'border-rose-500/20' },
]

const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
        return (
            <div className="glass-card p-3 !bg-slate-900/90 border border-white/10 shadow-xl">
                <p className="text-slate-200 font-bold mb-2">{label}</p>
                {payload.map((entry: any, index: number) => (
                    <p key={index} className="text-sm" style={{ color: entry.color }}>
                        {entry.name}: {entry.value}
                    </p>
                ))}
            </div>
        );
    }
    return null;
};

const ModernAnalyticsScreen: React.FC = () => {
    const { t } = useAppContext();
    const navigate = useNavigate();

    return (
        <div className="space-y-8 animate-contentFadeIn">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-bold modern-gradient-text flex items-center gap-3">
                        <BarChart2 className="text-cyan-400" size={32} />
                        {t('analytics')}
                    </h1>
                    <p className="text-slate-400 mt-1">لوحة تحكم تحليلية شاملة لأداء النظام والموظفين</p>
                </div>
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1 p-1 bg-white/5 border border-white/10 rounded-xl">
                        <button className="px-3 py-1.5 text-xs font-medium rounded-lg bg-cyan-500/20 text-cyan-300 shadow-sm">أسبوع</button>
                        <button className="px-3 py-1.5 text-xs font-medium rounded-lg text-slate-400 hover:text-slate-200 hover:bg-white/5 transition-colors">شهر</button>
                        <button className="px-3 py-1.5 text-xs font-medium rounded-lg text-slate-400 hover:text-slate-200 hover:bg-white/5 transition-colors">سنة</button>
                    </div>
                    <select className="bg-white/5 border border-white/10 rounded-xl text-sm py-2 px-3 text-slate-300 focus:ring-1 focus:ring-cyan-500/50 focus:border-cyan-500/50 focus:outline-none">
                        <option>جميع الفروع</option>
                        <option>الرياض</option>
                        <option>جدة</option>
                    </select>
                    <button
                        onClick={() => navigate('/')}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 transition-all hover:text-white"
                    >
                        <ArrowRight size={18} />
                        <span>رجوع</span>
                    </button>
                </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {kpiCards.map(({ title, value, icon: Icon, color, bg, border }, idx) => (
                    <div
                        key={title}
                        className={`glass-card p-4 flex flex-col items-center justify-center text-center group hover:-translate-y-1 transition-transform duration-300 animate-fadeInUp`}
                        style={{ animationDelay: `${idx * 50}ms` }}
                    >
                        <div className={`p-3 rounded-xl ${bg} ${border} border mb-3 group-hover:scale-110 transition-transform`}>
                            <Icon className={color} size={24} />
                        </div>
                        <p className="text-2xl font-bold text-white mb-1">{value}</p>
                        <p className="text-xs text-slate-400">{title}</p>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Employee Performance */}
                <div className="glass-card p-6 animate-fadeInUp" style={{ animationDelay: '300ms' }}>
                    <div className="flex items-center gap-2 mb-6">
                        <Activity className="text-cyan-400" size={20} />
                        <h3 className="text-lg font-bold text-white">أداء الموظفين</h3>
                    </div>
                    <div className="h-[300px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={employeePerformanceData}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.05)" />
                                <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} />
                                <YAxis stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} />
                                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.05)' }} />
                                <Legend wrapperStyle={{ paddingTop: '20px' }} />
                                <Bar dataKey="التقارير المقدمة" fill="#818cf8" radius={[4, 4, 0, 0]} barSize={20} />
                                <Bar dataKey="أعمال الصيانة" fill="#34d399" radius={[4, 4, 0, 0]} barSize={20} />
                                <Bar dataKey="أعمال التركيب" fill="#fbbf24" radius={[4, 4, 0, 0]} barSize={20} />
                                <Bar dataKey="الاستفسارات المعالجة" fill="#f472b6" radius={[4, 4, 0, 0]} barSize={20} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Weekly Revenue */}
                <div className="glass-card p-6 animate-fadeInUp" style={{ animationDelay: '400ms' }}>
                    <div className="flex items-center gap-2 mb-6">
                        <TrendingUp className="text-emerald-400" size={20} />
                        <h3 className="text-lg font-bold text-white">الإيرادات الأسبوعية</h3>
                    </div>
                    <div className="h-[300px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={revenueData}>
                                <defs>
                                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#818cf8" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="#818cf8" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.05)" />
                                <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} />
                                <YAxis stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend wrapperStyle={{ paddingTop: '20px' }} />
                                <Area type="monotone" dataKey="الإيرادات" stroke="#818cf8" strokeWidth={3} fillOpacity={1} fill="url(#colorRevenue)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Customer Feedback */}
                <div className="glass-card p-6 animate-fadeInUp" style={{ animationDelay: '500ms' }}>
                    <div className="flex items-center gap-2 mb-6">
                        <PieChartIcon className="text-purple-400" size={20} />
                        <h3 className="text-lg font-bold text-white">ملاحظات العملاء</h3>
                    </div>
                    <div className="h-[300px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={customerFeedbackData}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={100}
                                    paddingAngle={5}
                                    stroke="none"
                                >
                                    {customerFeedbackData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={FEEDBACK_COLORS[index % FEEDBACK_COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip content={<CustomTooltip />} />
                                <Legend wrapperStyle={{ paddingTop: '20px' }} iconType="circle" />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Branch Revenue */}
                <div className="glass-card p-6 animate-fadeInUp" style={{ animationDelay: '600ms' }}>
                    <div className="flex items-center gap-2 mb-6">
                        <DollarSign className="text-amber-400" size={20} />
                        <h3 className="text-lg font-bold text-white">إيرادات الفروع</h3>
                    </div>
                    <div className="h-[300px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={branchRevenueData} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="rgba(255,255,255,0.05)" />
                                <XAxis type="number" stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} />
                                <YAxis dataKey="name" type="category" stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} width={80} />
                                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.05)' }} />
                                <Legend wrapperStyle={{ paddingTop: '20px' }} />
                                <Bar dataKey="الإيرادات" fill="#34d399" radius={[0, 4, 4, 0]} barSize={30} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ModernAnalyticsScreen;
